import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { RecertFormField } from 'components/Assessments/RecertAssessment/types/RecertFormField'
import { RecertFormData } from 'components/Assessments/RecertAssessment/types/RecertFormData'
import { RecertAssessmentQuestionnaireItem } from 'components/Assessments/RecertAssessment/types/questionnaire'
import { AdapterOptions, SectionAdapter } from '../../../../../adapters/types'
import { getAnswerValuesFromItems } from '../../../../../helpers'

/**
 * Risk Assessment subsection adapter
 * Handles complex risk assessments including ScoredChecklist components (18 fields)
 * This is one of the more complex subsections with various risk factors and safety measures
 */
export class RiskAssessmentAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hospitalization risk assessment
    const hospitalizationRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK
    )?.at(0)

    const hospitalizationRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
    )

    const hospitalizationRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
    )?.at(0)

    // Emergency planning
    const triageCode = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE
    )?.at(0)

    const emergencyPlan = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN
    )?.at(0)

    const safeRoom = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM
    )

    // Fire safety assessment
    const fireAssessmentOxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN
    )?.at(0)

    const fireSafetyPrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS
    )

    // Homebound status
    const homeboundStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS
    )?.at(0)

    // Infection risk assessment
    const infectionRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK
    )?.at(0)

    const infectionRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS
    )

    const infectionRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
    )?.at(0)

    // Fall risk assessment (ScoredChecklist component)
    const fallRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK
    )?.at(0)

    const fallRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS
    )

    const fallRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER
    )?.at(0)

    const fallRiskAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT
    )

    // Financial and environmental safety
    const financeRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK
    )?.at(0)

    const financeRiskOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER
    )?.at(0)

    const environmentSafety = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY
    )

    const nurseSafetyRisks = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS
    )

    const safetyMeasures = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES
    )

    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES
    )?.at(0)

    return {
      [RecertFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK]: hospitalizationRisk,
      [RecertFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS]: hospitalizationRiskFactors,
      [RecertFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER]:
        hospitalizationRiskFactorsOther,
      [RecertFormField.RISK_ASSESSMENT_TRIAGE_CODE]: triageCode,
      [RecertFormField.RISK_ASSESSMENT_EMERGENCY_PLAN]: emergencyPlan,
      [RecertFormField.RISK_ASSESSMENT_SAFE_ROOM]: safeRoom,
      [RecertFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN]: fireAssessmentOxygen,
      [RecertFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS]: fireSafetyPrecautions,
      [RecertFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS]: homeboundStatus,
      [RecertFormField.RISK_ASSESSMENT_INFECTION_RISK]: infectionRisk,
      [RecertFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS]: infectionRiskFactors,
      [RecertFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER]: infectionRiskFactorsOther,
      [RecertFormField.RISK_ASSESSMENT_FALL_RISK]: fallRisk,
      [RecertFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS]: fallRiskFactors,
      [RecertFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER]: fallRiskFactorsOther,
      [RecertFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT]: fallRiskAssessment,
      [RecertFormField.RISK_ASSESSMENT_FINANCE_RISK]: financeRisk,
      [RecertFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER]: financeRiskOther,
      [RecertFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY]: environmentSafety,
      [RecertFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS]: nurseSafetyRisks,
      [RecertFormField.RISK_ASSESSMENT_SAFETY_MEASURES]: safetyMeasures,
      [RecertFormField.RISK_ASSESSMENT_NOTES]: notes
    }
  }

  buildFlatAnswers(formData: RecertFormData): FlatAnswerMutation[] {
    return [
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK,
        value: formData[RecertFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS,
        value: formData[RecertFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER,
        value: formData[RecertFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE,
        value: formData[RecertFormField.RISK_ASSESSMENT_TRIAGE_CODE]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN,
        value: formData[RecertFormField.RISK_ASSESSMENT_EMERGENCY_PLAN]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM,
        value: formData[RecertFormField.RISK_ASSESSMENT_SAFE_ROOM]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN,
        value: formData[RecertFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS,
        value: formData[RecertFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS,
        value: formData[RecertFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK,
        value: formData[RecertFormField.RISK_ASSESSMENT_INFECTION_RISK]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS,
        value: formData[RecertFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER,
        value: formData[RecertFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK,
        value: formData[RecertFormField.RISK_ASSESSMENT_FALL_RISK]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS,
        value: formData[RecertFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER,
        value: formData[RecertFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT,
        value: formData[RecertFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK,
        value: formData[RecertFormField.RISK_ASSESSMENT_FINANCE_RISK]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER,
        value: formData[RecertFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY,
        value: formData[RecertFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS,
        value: formData[RecertFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS]
      },
      {
        linkId:
          RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES,
        value: formData[RecertFormField.RISK_ASSESSMENT_SAFETY_MEASURES]
      },
      {
        linkId: RecertAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES,
        value: formData[RecertFormField.RISK_ASSESSMENT_NOTES]
      }
    ]
  }
}
